// Step 7: the poster staff scan to join. The QR code only exists once the site's set-up
// has been approved on its ops tablet, and it never changes after that, so posters don't
// need replacing.

import { useState } from 'react';
import { POSTER_STEPS, posterPdf } from '../../../../src/poster.ts';
import { message } from '../../api.ts';
import { QRCode, qrPng, saveFile } from '../../qr.tsx';
import type { SiteView } from '../../types.ts';

const fileName = (site: SiteView, what: string, ext: string) => `ZAS ${what} - ${site.siteName.replace(/[\\/:*?"<>|]+/g, ' ').trim()}.${ext}`;

export function PosterStep({ site }: { site: SiteView }) {
  const [error, setError] = useState<string | null>(null);
  if (!site.qr) {
    return (
      <div className="note amber" style={{ maxWidth: 760 }}>
        The QR code is made when the set-up is approved on the ops tablet. Come back to this step after it's approved.
      </div>
    );
  }
  const qr = site.qr;
  const pdf = () => {
    setError(null);
    try {
      saveFile(new Blob([posterPdf(site.siteName, qr)], { type: 'application/pdf' }), fileName(site, 'poster', 'pdf'));
    } catch (e) {
      setError(message(e));
    }
  };
  const png = async () => {
    setError(null);
    try {
      saveFile(await qrPng(qr), fileName(site, 'QR code', 'png'));
    } catch (e) {
      setError(message(e));
    }
  };
  return (
    <div className="poster-step">
      <div className="poster" aria-label="Poster preview">
        <span className="poster-mark">Z.A.S.</span>
        <span className="poster-title">Safety alerts for {site.siteName}</span>
        <div style={{ alignSelf: 'center' }}>
          <QRCode text={qr} px={200} label={`QR code to join ${site.siteName}`} />
        </div>
        <ol>
          {POSTER_STEPS.map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ol>
      </div>
      <div className="stack" style={{ gap: 14, maxWidth: 520 }}>
        <p className="muted">Put the poster up where staff pass. The code only lets a phone join this site. It can't be used to send anything.</p>
        <div className="row wrap">
          <button className="btn primary" onClick={pdf}>
            Download poster (PDF)
          </button>
          <button className="btn" onClick={png}>
            Download QR code
          </button>
        </div>
        <p className="faint small">A4. The code stays the same when the site's set-up changes later, so the posters don't need replacing.</p>
        {error && <p className="error">{error}</p>}
      </div>
    </div>
  );
}
